import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { SectionLoader, EmptyState } from "@/components/common";
import { EventPeopleEditor } from "@/components/events/EventPeopleEditor";
import { formatDate } from "@/lib/format";
import { fridayInfo, ordinalWord } from "@/lib/fridays";
import { CalendarDays, Plus, Pencil, Trash2, Users, Loader2, CheckCircle2, Hourglass, Info, Sparkles, Upload, Image as ImageIcon, X, Share2 } from "lucide-react";
import ShareKit from "@/components/admin/ShareKit";

const EMPTY = { title: "", theme_title: "", theme_description: "", description: "", date: "", start_time: "12:00", end_time: "15:00", location_id: "", flyer_image: "", flyer_square: "", flyer_story: "", rsvp_capacity: 10, rsvp_enabled: true, is_published: true, is_featured: false, people: [] };

export default function AdminEvents() {
  const [events, setEvents] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [tmpl, setTmpl] = useState("");
  const [flyerUploading, setFlyerUploading] = useState(false);
  const [flyerGenerating, setFlyerGenerating] = useState(false);
  const [styleHint, setStyleHint] = useState("");
  const [headcounts, setHeadcounts] = useState({});
  const [peopleFor, setPeopleFor] = useState(null); // event whose "coming" list is open
  const [kit, setKit] = useState(null); // share kit for the flyer currently in the editor
  const [kitFor, setKitFor] = useState(null); // { event, kit } for the table "Share kit" dialog
  const [kitLoading, setKitLoading] = useState(false);

  const load = useCallback(async () => {
    const [ev, loc, tpl] = await Promise.all([
      api.get("/events/admin/all").then((r) => r.data).catch(() => []),
      api.get("/locations").then((r) => r.data).catch(() => []),
      api.get("/events/templates").then((r) => r.data).catch(() => []),
    ]);
    setEvents(ev); setLocations(loc); setTemplates(tpl); setLoading(false);
    const dates = [...new Set(ev.map((e) => e.date).filter(Boolean))];
    if (dates.length) api.get("/attending/summary", { params: { dates: dates.join(",") } }).then((r) => setHeadcounts(r.data || {})).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const applyTemplate = (v) => {
    const t = templates.find((x) => String(x.friday_ordinal) === String(v));
    if (!t) return;
    setTmpl(String(v));
    setForm((f) => ({
      ...EMPTY,
      date: f.date,
      title: t.title,
      theme_title: t.theme_title,
      theme_description: t.theme_description,
      description: t.description,
      location_id: t.location_id || "",
      start_time: t.start_time,
      end_time: t.end_time,
      rsvp_capacity: t.rsvp_capacity,
    }));
    toast.success(`Loaded the ${t.label} template`);
  };

  const openCreate = () => { setEditing(null); setForm(EMPTY); setTmpl(""); setKit(null); setOpen(true); };
  const openKit = async (ev) => {
    setKitLoading(true);
    try { const r = await api.get(`/events/${ev.event_id}/share-kit`); setKitFor({ event: ev, kit: r.data }); }
    catch (e) { toast.error("Couldn't load the share kit"); }
    finally { setKitLoading(false); }
  };
  const openEdit = (ev) => {
    setEditing(ev);
    setTmpl("");
    setForm({ ...EMPTY, ...ev, location_id: ev.location_id || "", people: ev.people || [] });
    setKit(null);
    if (ev.flyer_image) api.get(`/events/${ev.event_id}/share-kit`).then((r) => setKit(r.data)).catch(() => {});
    setOpen(true);
  };

  const save = async () => {
    if (!form.title) { toast.error("Title is required"); return; }
    setSaving(true);
    const payload = { ...form, location_id: form.location_id || null, rsvp_capacity: parseInt(form.rsvp_capacity, 10) || 0 };
    try {
      if (editing) await api.put(`/events/${editing.event_id}`, payload);
      else await api.post("/events", payload);
      toast.success(editing ? "Event updated" : "Event created");
      setOpen(false); await load();
    } catch (e) { toast.error("Could not save event"); } finally { setSaving(false); }
  };

  const remove = async (ev) => {
    try { await api.delete(`/events/${ev.event_id}`); toast.success("Event deleted"); await load(); }
    catch (e) { toast.error("Could not delete"); }
  };

  const uploadFlyer = async (file) => {
    if (!file) return;
    if (!file.type?.startsWith("image/")) { toast.error("Please choose an image file"); return; }
    setFlyerUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("prefix", "event-flyers");
      const res = await api.post("/storage/upload", fd);
      setForm((f) => ({ ...f, flyer_image: res.data.url }));
      toast.success("Flyer uploaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed — try a smaller image or paste a URL");
    } finally { setFlyerUploading(false); }
  };

  const generateFlyer = async () => {
    if (!form.title && !form.theme_title) { toast.error("Add a title or theme first"); return; }
    setFlyerGenerating(true);
    try {
      const payload = { ...form, location_id: form.location_id || null, rsvp_capacity: parseInt(form.rsvp_capacity, 10) || 0, style_hint: styleHint || null };
      const res = await api.post("/events/flyer/generate", payload, { timeout: 150000 });
      setForm((f) => ({ ...f, flyer_image: res.data.url, flyer_square: res.data.square?.url || "", flyer_story: res.data.story?.url || "" }));
      setKit(res.data);
      toast.success(res.data.art_source === "ai" ? "Share kit designed — save the event to keep it" : "Share kit created (brand background) — save the event to keep it");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't generate a flyer right now");
    } finally { setFlyerGenerating(false); }
  };

  if (loading) return <SectionLoader />;

  const fi = fridayInfo(form.date);
  const suggested = fi?.isFriday && fi.ordinal >= 1 && fi.ordinal <= 4
    ? templates.find((t) => t.friday_ordinal === fi.ordinal) : null;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div><h1 className="font-serif text-2xl font-semibold text-foreground sm:text-3xl">Events</h1><p className="mt-1 text-muted-foreground">Create and manage weekly meetups & podcast sessions.</p></div>
        <Button onClick={openCreate} className="bg-primary text-primary-foreground hover:bg-[hsl(var(--primary-hover))]" data-testid="admin-events-create-button"><Plus className="mr-2 h-4 w-4" />New event</Button>
      </div>

      {events.length === 0 ? (
        <EmptyState icon={CalendarDays} title="No events yet" description="Create your first Friday Conversations event." action={<Button onClick={openCreate}><Plus className="mr-2 h-4 w-4" />New event</Button>} />
      ) : (
        <Card className="border-border bg-card"><CardContent className="p-0">
          <Table data-testid="admin-events-table">
            <TableHeader><TableRow><TableHead>Event</TableHead><TableHead>Date</TableHead><TableHead>Coming</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
            <TableBody>
              {events.map((ev) => (
                <TableRow key={ev.event_id} className="hover:bg-secondary/40">
                  <TableCell>
                    <div className="font-medium text-foreground">{ev.title}</div>
                    {ev.theme_title && <div className="text-xs text-muted-foreground">{ev.theme_title}</div>}
                  </TableCell>
                  <TableCell className="text-muted-foreground">{ev.date ? formatDate(ev.date) : "—"}</TableCell>
                  <TableCell>
                    {ev.date && headcounts[ev.date]?.count > 0 ? (
                      <Button size="sm" variant="secondary" className="h-7 rounded-full px-2.5" onClick={() => setPeopleFor(ev)} data-testid={`admin-event-headcount-${ev.event_id}`}>
                        <Users className="mr-1.5 h-3.5 w-3.5 text-primary" /> {headcounts[ev.date].count}
                      </Button>
                    ) : (
                      <span className="text-xs text-muted-foreground" data-testid={`admin-event-headcount-${ev.event_id}`}>—</span>
                    )}
                  </TableCell>
                  <TableCell><div className="flex flex-wrap gap-1">{ev.is_published ? <Badge variant="secondary">Published</Badge> : <Badge variant="outline">Draft</Badge>}{ev.is_featured && <Badge className="border border-[rgba(201,119,46,0.45)] bg-[rgba(201,119,46,0.22)] text-[#8A4A18]">Featured</Badge>}</div></TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      {ev.flyer_image && <Button size="icon" variant="ghost" onClick={() => openKit(ev)} title="Share kit" disabled={kitLoading} data-testid={`admin-event-share-kit-${ev.event_id}`}><Share2 className="h-4 w-4" /></Button>}
                      <Button size="icon" variant="ghost" onClick={() => openEdit(ev)} data-testid="admin-event-edit"><Pencil className="h-4 w-4" /></Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild><Button size="icon" variant="ghost" className="text-destructive" data-testid="admin-event-delete"><Trash2 className="h-4 w-4" /></Button></AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader><AlertDialogTitle>Delete this event?</AlertDialogTitle><AlertDialogDescription>This removes the event and all its RSVPs. This cannot be undone.</AlertDialogDescription></AlertDialogHeader>
                          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => remove(ev)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Delete</AlertDialogAction></AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent></Card>
      )}

      {/* Share kit (from the table) */}
      <Dialog open={!!kitFor} onOpenChange={(o) => !o && setKitFor(null)}>
        <DialogContent className="sm:max-w-2xl" data-testid="admin-share-kit-dialog">
          <DialogHeader><DialogTitle className="font-serif text-xl">Share kit</DialogTitle></DialogHeader>
          {kitFor && (
            <div>
              <p className="text-sm text-muted-foreground">{kitFor.event.title} · {formatDate(kitFor.event.date)}</p>
              {!kitFor.kit.square && <p className="mt-2 rounded-lg bg-secondary px-3 py-2 text-xs text-muted-foreground" data-testid="admin-share-kit-missing-sizes">This flyer was uploaded, so only the portrait size exists. Open the event and click <span className="font-medium text-foreground">Generate flyer</span> to get square and story versions.</p>}
              <div className="mt-4"><ShareKit kit={kitFor.kit} /></div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Who's coming */}
      <Dialog open={!!peopleFor} onOpenChange={(o) => !o && setPeopleFor(null)}>
        <DialogContent className="sm:max-w-md" data-testid="admin-headcount-dialog">
          <DialogHeader><DialogTitle className="font-serif text-xl">Who&rsquo;s coming</DialogTitle></DialogHeader>
          {peopleFor && (
            <div>
              <p className="text-sm text-muted-foreground">{peopleFor.title} · {formatDate(peopleFor.date)}</p>
              <p className="mt-2 text-sm"><span className="font-semibold text-foreground" data-testid="admin-headcount-total">{headcounts[peopleFor.date]?.count || 0}</span> tapped &ldquo;I&rsquo;ll be there&rdquo; on the homepage. No RSVP required — this is a soft headcount.</p>
              <ul className="mt-4 max-h-72 divide-y divide-border overflow-y-auto rounded-lg border border-border" data-testid="admin-headcount-list">
                {(headcounts[peopleFor.date]?.people || []).map((p, i) => (
                  <li key={i} className="flex items-center justify-between px-3 py-2 text-sm">
                    <span className="font-medium text-foreground">{p.name || "Guest"}</span>
                    <span className="text-xs text-muted-foreground">{p.member ? <Badge variant="secondary">Member</Badge> : "Visitor"}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create/Edit Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader><DialogTitle className="font-serif text-xl">{editing ? "Edit event" : "New event"}</DialogTitle></DialogHeader>
          <div className="grid gap-4 sm:grid-cols-2">
            {!editing && templates.length > 0 && (
              <div className="sm:col-span-2 rounded-lg border border-dashed border-border bg-secondary/40 p-3">
                <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Start from a Friday template</Label>
                <Select value={tmpl} onValueChange={applyTemplate}>
                  <SelectTrigger className="mt-1.5" data-testid="admin-event-template-select"><SelectValue placeholder="Choose a template (optional)" /></SelectTrigger>
                  <SelectContent>
                    {templates.map((t) => <SelectItem key={t.friday_ordinal} value={String(t.friday_ordinal)}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="sm:col-span-2"><Label>Title</Label><Input className="mt-1.5" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="admin-event-title-input" /></div>
            <div><Label>Weekly theme</Label><Input className="mt-1.5" value={form.theme_title} onChange={(e) => setForm({ ...form, theme_title: e.target.value })} /></div>
            <div><Label>Date</Label><Input type="date" className="mt-1.5" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="admin-event-date-input" /></div>
            <div><Label>Start time</Label><Input type="time" className="mt-1.5" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} /></div>
            <div><Label>End time</Label><Input type="time" className="mt-1.5" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} /></div>

            {form.date && fi && !fi.isFriday && (
              <Alert className="sm:col-span-2" data-testid="admin-event-date-note">
                <Info className="h-4 w-4" />
                <AlertTitle>That date isn't a Friday</AlertTitle>
                <AlertDescription>Friday Conversations meet on Fridays — double-check the date.</AlertDescription>
              </Alert>
            )}
            {form.date && fi?.isFriday && fi.ordinal === 5 && (
              <Alert className="sm:col-span-2" data-testid="admin-event-fifth-friday-note">
                <Info className="h-4 w-4" />
                <AlertTitle>5th Friday — Core team meeting</AlertTitle>
                <AlertDescription>There's usually no public gathering on a 5th Friday; this time is reserved for the Core team.</AlertDescription>
              </Alert>
            )}
            {!editing && suggested && (
              <div className="sm:col-span-2 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border bg-secondary/50 p-3" data-testid="admin-event-suggested-template">
                <span className="text-sm text-muted-foreground">This is the {ordinalWord(fi.ordinal)} Friday — load the <span className="font-medium text-foreground">{suggested.theme_title}</span> template?</span>
                <Button size="sm" variant="secondary" onClick={() => applyTemplate(String(suggested.friday_ordinal))} data-testid="admin-event-use-suggested-template"><Sparkles className="mr-1.5 h-3.5 w-3.5" />Use template</Button>
              </div>
            )}

            <div className="sm:col-span-2"><Label>Location</Label>
              <Select value={form.location_id || "none"} onValueChange={(v) => { const loc = locations.find((l) => l.location_id === v); setForm({ ...form, location_id: v === "none" ? "" : v, ...(loc ? { start_time: loc.start_time || "12:00", end_time: loc.end_time || "15:00" } : {}) }); }}>
                <SelectTrigger className="mt-1.5"><SelectValue placeholder="Choose a location" /></SelectTrigger>
                <SelectContent><SelectItem value="none">No location</SelectItem>{locations.map((l) => <SelectItem key={l.location_id} value={l.location_id}>{l.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2"><Label>Theme description</Label><Textarea className="mt-1.5" rows={2} value={form.theme_description} onChange={(e) => setForm({ ...form, theme_description: e.target.value })} /></div>
            <div className="sm:col-span-2"><Label>Event description</Label><Textarea className="mt-1.5" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>

            <div className="sm:col-span-2 rounded-lg border border-border p-3">
              <Label className="flex items-center gap-1.5 text-sm font-semibold"><ImageIcon className="h-4 w-4 text-primary" />Event flyer / poster</Label>
              <p className="mt-0.5 text-xs text-muted-foreground">Generate one in a click from the theme, date, venue and guests — or upload your designer&rsquo;s poster. Portrait posters look best.</p>
              <div className="mt-3 flex items-start gap-3">
                {form.flyer_image ? (
                  <div className="relative shrink-0">
                    <img src={form.flyer_image} alt="Event flyer preview" className="h-40 w-32 rounded-lg border border-border bg-secondary/40 object-contain" data-testid="admin-event-flyer-preview" />
                    <Button type="button" size="icon" variant="secondary" className="absolute -right-2 -top-2 h-7 w-7 rounded-full border border-border shadow" onClick={() => { setForm({ ...form, flyer_image: "", flyer_square: "", flyer_story: "" }); setKit(null); }} aria-label="Remove flyer" data-testid="admin-event-flyer-remove"><X className="h-4 w-4" /></Button>
                  </div>
                ) : (
                  <div className="flex h-40 w-32 shrink-0 items-center justify-center rounded-lg border border-dashed border-border bg-secondary/40 text-muted-foreground">
                    {flyerGenerating ? <Loader2 className="h-7 w-7 animate-spin text-primary" /> : <ImageIcon className="h-7 w-7" />}
                  </div>
                )}
                <div className="flex-1 space-y-2">
                  <div className="rounded-lg border border-primary/30 bg-primary/5 p-2.5" data-testid="admin-event-flyer-generator">
                    <div className="flex flex-wrap items-center gap-2">
                      <Button type="button" size="sm" onClick={generateFlyer} disabled={flyerGenerating} className="bg-primary text-primary-foreground hover:bg-[hsl(var(--primary-hover))]" data-testid="admin-event-flyer-generate">
                        {flyerGenerating ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Sparkles className="mr-1.5 h-3.5 w-3.5" />}
                        {flyerGenerating ? "Designing…" : form.flyer_image ? "Regenerate flyer" : "Generate flyer"}
                      </Button>
                      <Input value={styleHint} onChange={(e) => setStyleHint(e.target.value)} placeholder="Optional art direction, e.g. “warm gallery lights”" className="h-8 flex-1 min-w-[180px] text-xs" data-testid="admin-event-flyer-style" />
                    </div>
                    <p className="mt-1.5 text-[11px] text-muted-foreground">{flyerGenerating ? "Painting the background and laying out the text — about 15 seconds." : "AI paints an abstract brand background; the date, venue and guest names are typeset exactly."}</p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <Button type="button" variant="outline" size="sm" asChild>
                      <label className="cursor-pointer" data-testid="admin-event-flyer-upload-label">
                        {flyerUploading ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Upload className="mr-1.5 h-3.5 w-3.5" />}
                        {form.flyer_image ? "Replace with upload" : "Upload flyer"}
                        <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadFlyer(e.target.files?.[0])} data-testid="admin-event-flyer-input" />
                      </label>
                    </Button>
                    {form.flyer_image && (
                      <Button type="button" variant="ghost" size="sm" asChild><a href={form.flyer_image} download target="_blank" rel="noopener noreferrer" data-testid="admin-event-flyer-download">Open / download</a></Button>
                    )}
                  </div>
                  <Input value={form.flyer_image || ""} onChange={(e) => setForm({ ...form, flyer_image: e.target.value })} placeholder="…or paste an image URL" className="h-9" data-testid="admin-event-flyer-url" />
                </div>
              </div>
              {kit && form.flyer_image && (
                <div className="mt-4 border-t border-border pt-4" data-testid="admin-event-share-kit">
                  <div className="mb-2 flex items-center gap-1.5 text-sm font-semibold"><Share2 className="h-4 w-4 text-primary" /> Share kit <span className="text-xs font-normal text-muted-foreground">— square + story sizes and copy-ready captions</span></div>
                  <ShareKit kit={{ ...kit, portrait: kit.portrait || { url: form.flyer_image } }} compact />
                </div>
              )}
            </div>

            <div className="flex items-center justify-between rounded-lg border border-border p-3"><Label>Published</Label><Switch checked={form.is_published} onCheckedChange={(v) => setForm({ ...form, is_published: v })} /></div>
            <div className="flex items-center justify-between rounded-lg border border-border p-3"><Label>Featured on landing</Label><Switch checked={form.is_featured} onCheckedChange={(v) => setForm({ ...form, is_featured: v })} /></div>
            <div className="sm:col-span-2 border-t border-border pt-4">
              <EventPeopleEditor value={form.people} onChange={(people) => setForm({ ...form, people })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-primary text-primary-foreground hover:bg-[hsl(var(--primary-hover))]" data-testid="admin-event-save-button">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save event"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
